import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { ApiClientError } from '@/lib/api/client'
import {
  applyDoeyUserIdAfterUserSearch,
  canSubmitWithDoeyPayout,
  DOEY_GATEWAY_REQUIRED_MESSAGE,
} from '@/lib/institution-doey-payout'
import {
  resolveMaxAffiliateCommissionPercent,
  searchMyDistributorAffiliateUser,
} from '@/lib/my-distributor'
import type { AffiliatePayoutMethod, PlatformUser } from '@/types/affiliates'
import type {
  CreateMyDistributorAffiliateInput,
  DistributorAllowedCampaign,
  MyDistributorAffiliateRegistration,
  MyDistributorProgram,
  UpdateMyDistributorAffiliateInput,
} from '@/types/my-distributor'
import modalStyles from '@/pages/AfiliadosPage/AfiliadosListaPage.module.css'

function parseCommissionPercent(value: string): number | null {
  const num = parseFloat(value.replace(',', '.'))
  if (Number.isNaN(num) || num < 0.1 || num > 80) {
    return null
  }
  return num
}

interface DistributorAffiliateRegistrationModalProps {
  program: MyDistributorProgram
  registration: MyDistributorAffiliateRegistration | null
  workspaceCampaignId?: number | null
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (
    data: CreateMyDistributorAffiliateInput | UpdateMyDistributorAffiliateInput,
    registrationId?: number
  ) => Promise<void>
}

export function DistributorAffiliateRegistrationModal({
  program,
  registration,
  workspaceCampaignId = null,
  isSubmitting,
  onClose,
  onSubmit,
}: DistributorAffiliateRegistrationModalProps) {
  const isEdit = registration !== null
  const linkCampaigns = program.allowed_campaigns

  const [searchEmail, setSearchEmail] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null)
  const [isSearchingUser, setIsSearchingUser] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [payoutMethod, setPayoutMethod] = useState<AffiliatePayoutMethod>('doey')
  const [doeyUserId, setDoeyUserId] = useState('')
  const [commissionPercent, setCommissionPercent] = useState(
    registration ? String(registration.commission_percent) : ''
  )
  const [isActive, setIsActive] = useState(registration?.is_active ?? true)
  const [allCampaigns, setAllCampaigns] = useState(registration?.all_campaigns ?? true)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>(
    () => registration?.allowed_campaigns.map((campaign) => campaign.id) ?? []
  )
  const [campaignCommissions, setCampaignCommissions] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {}
    for (const campaign of registration?.allowed_campaigns ?? []) {
      if (campaign.commission_percent !== undefined) {
        initial[campaign.id] = String(campaign.commission_percent)
      }
    }
    return initial
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [gatewayAlert, setGatewayAlert] = useState<string | null>(null)

  useEffect(() => {
    const sync = window.setTimeout(() => {
      if (registration) {
        setCommissionPercent(String(registration.commission_percent))
        setIsActive(registration.is_active)
        setAllCampaigns(registration.all_campaigns ?? true)
        setSelectedCampaignIds((registration.allowed_campaigns ?? []).map((campaign) => campaign.id))
        const commissions: Record<number, string> = {}
        for (const campaign of registration.allowed_campaigns ?? []) {
          if (campaign.commission_percent !== undefined) {
            commissions[campaign.id] = String(campaign.commission_percent)
          }
        }
        setCampaignCommissions(commissions)
      } else {
        setPayoutMethod('doey')
        setDoeyUserId('')
        setCommissionPercent('')
        setIsActive(true)
        if (workspaceCampaignId != null) {
          setAllCampaigns(false)
          setSelectedCampaignIds([workspaceCampaignId])
          setCampaignCommissions({})
        } else {
          setAllCampaigns(true)
          setSelectedCampaignIds([])
          setCampaignCommissions({})
        }
        setSelectedUser(null)
        setSearchEmail('')
        setSearchPhone('')
        setSearchError(null)
      }
    }, 0)
    return () => window.clearTimeout(sync)
  }, [registration, workspaceCampaignId])

  useEffect(() => {
    if (isEdit || payoutMethod !== 'doey') {
      if (!isEdit) {
        setDoeyUserId('')
      }
      setGatewayAlert(null)
      return
    }
    if (selectedUser) {
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, selectedUser.doey_user_id)
      setDoeyUserId(doeyApply.ok ? doeyApply.doeyUserId : '')
      setGatewayAlert(doeyApply.ok ? null : doeyApply.gatewayError)
      return
    }
    setDoeyUserId('')
    setGatewayAlert(null)
  }, [isEdit, payoutMethod, selectedUser])

  const maxCapForCampaign = (campaignId: number): number =>
    resolveMaxAffiliateCommissionPercent(program, campaignId)

  const globalCap = program.max_affiliate_commission_percent

  const handleSearchUser = async () => {
    const email = searchEmail.trim()
    const phone = searchPhone.replace(/\D/g, '')
    if (!email && !phone) {
      setSearchError('Informe o e-mail ou o telefone para procurar.')
      setSelectedUser(null)
      return
    }
    setIsSearchingUser(true)
    setSearchError(null)
    try {
      const res = await searchMyDistributorAffiliateUser(program.distributor_id, {
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      })
      setSelectedUser(res.user)
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, res.user.doey_user_id)
      if (!doeyApply.ok) {
        setGatewayAlert(doeyApply.gatewayError)
        setDoeyUserId('')
      } else {
        setGatewayAlert(null)
        if (payoutMethod === 'doey') {
          setDoeyUserId(doeyApply.doeyUserId)
        }
      }
    } catch (err) {
      setSelectedUser(null)
      setGatewayAlert(null)
      setSearchError(err instanceof ApiClientError ? err.message : 'Usuário não encontrado.')
    } finally {
      setIsSearchingUser(false)
    }
  }

  const globalCommissionNum = parseCommissionPercent(commissionPercent)

  const toggleCampaignId = (campaignId: number) => {
    setSelectedCampaignIds((prev) => {
      if (prev.includes(campaignId)) {
        setCampaignCommissions((commissions) => {
          const next = { ...commissions }
          delete next[campaignId]
          return next
        })
        return prev.filter((id) => id !== campaignId)
      }
      return [...prev, campaignId]
    })
  }

  const setCampaignCommission = (campaignId: number, value: string) => {
    setCampaignCommissions((prev) => ({ ...prev, [campaignId]: value }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormError(null)

    if (!isEdit && !selectedUser) {
      setFormError('Procure e selecione um usuário da plataforma.')
      return
    }
    if (!isEdit && !canSubmitWithDoeyPayout(payoutMethod, selectedUser?.doey_user_id)) {
      setFormError(DOEY_GATEWAY_REQUIRED_MESSAGE)
      return
    }
    if (allCampaigns) {
      if (globalCommissionNum === null) {
        setFormError(`A comissão deve ser entre 0,1% e ${globalCap}%.`)
        return
      }
      if (globalCommissionNum > globalCap) {
        setFormError(`A comissão não pode exceder ${globalCap}% (teto do distribuidor).`)
        return
      }
    } else {
      if (selectedCampaignIds.length === 0) {
        setFormError('Selecione ao menos uma campanha ou marque Todas as Campanhas.')
        return
      }
      for (const campaignId of selectedCampaignIds) {
        const pct = parseCommissionPercent(campaignCommissions[campaignId] ?? '')
        const cap = maxCapForCampaign(campaignId)
        if (pct === null) {
          setFormError('Informe a comissão (0,1% a 80%) para cada campanha selecionada.')
          return
        }
        if (pct > cap) {
          setFormError(`A comissão na campanha selecionada não pode exceder ${cap}%.`)
          return
        }
      }
    }

    const campaignsPayload = allCampaigns
      ? undefined
      : selectedCampaignIds.map((campaignId) => ({
          campaign_id: campaignId,
          commission_percent: parseCommissionPercent(campaignCommissions[campaignId] ?? '')!,
        }))

    if (isEdit && registration) {
      const updateData: UpdateMyDistributorAffiliateInput = {
        is_active: isActive,
        all_campaigns: allCampaigns,
        ...(allCampaigns && globalCommissionNum !== null
          ? { commission_percent: globalCommissionNum }
          : {}),
        ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
      }
      await onSubmit(updateData, registration.id)
      return
    }

    const createData: CreateMyDistributorAffiliateInput = {
      platform_user_id: selectedUser!.id,
      payout_method: payoutMethod,
      all_campaigns: allCampaigns,
      is_active: isActive,
      ...(allCampaigns && globalCommissionNum !== null
        ? { commission_percent: globalCommissionNum }
        : {}),
      ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
    }
    await onSubmit(createData)
  }

  const renderCampaignCard = (campaign: DistributorAllowedCampaign) => {
    const isSelected = selectedCampaignIds.includes(campaign.id)
    const label = campaign.title?.trim() || campaign.name
    const cap = maxCapForCampaign(campaign.id)

    return (
      <div
        key={campaign.id}
        className={`${modalStyles.campaignPickCard} ${isSelected ? modalStyles.campaignPickCardActive : ''}`}
      >
        <label className={modalStyles.campaignPickCardLabel}>
          <input
            type="checkbox"
            className={modalStyles.payoutInputHidden}
            checked={isSelected}
            onChange={() => toggleCampaignId(campaign.id)}
          />
          <span className={modalStyles.campaignPickCardInner}>
            <Icon
              icon={
                isSelected ? 'mdi:checkbox-marked-circle' : 'mdi:checkbox-blank-circle-outline'
              }
              className={modalStyles.campaignPickCheckIcon}
              aria-hidden
            />
            <span className={modalStyles.campaignPickName}>
              {label}
              <small> (máx. {cap}%)</small>
            </span>
          </span>
        </label>
        {isSelected ? (
          <div className={modalStyles.campaignPickCommission}>
            <label
              className={modalStyles.campaignPickCommissionLabel}
              htmlFor={`distributor-campaign-commission-${campaign.id}`}
            >
              Comissão (%)
            </label>
            <input
              id={`distributor-campaign-commission-${campaign.id}`}
              className={modalStyles.campaignPickCommissionInput}
              type="number"
              step="0.1"
              min="0.1"
              max={cap}
              placeholder="10"
              value={campaignCommissions[campaign.id] ?? ''}
              onChange={(event) => setCampaignCommission(campaign.id, event.target.value)}
            />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="distributor-affiliate-modal-title"
    >
      <div className={modalStyles.modal}>
        <div className={modalStyles.modalHeader}>
          <h3 id="distributor-affiliate-modal-title" className={modalStyles.modalTitle}>
            {isEdit ? 'Editar afiliado' : 'Novo afiliado'}
          </h3>
          <button className={modalStyles.modalClose} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </div>

        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className={modalStyles.modalBody}>
            {!isEdit ? (
              <div className={modalStyles.field}>
                <span className={modalStyles.label}>Usuário da plataforma *</span>
                <p className={modalStyles.searchHint}>
                  Informe o e-mail ou o telefone do cadastro e clique em Procurar.
                </p>
                <div className={modalStyles.userSearchRow}>
                  <input
                    className={modalStyles.input}
                    type="email"
                    placeholder="E-mail"
                    value={searchEmail}
                    onChange={(event) => {
                      setSearchEmail(event.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <input
                    className={modalStyles.input}
                    type="tel"
                    placeholder="Telefone"
                    value={searchPhone}
                    onChange={(event) => {
                      setSearchPhone(event.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={modalStyles.searchUserBtn}
                    disabled={isSearchingUser}
                    onClick={() => void handleSearchUser()}
                  >
                    {isSearchingUser ? 'Procurando…' : 'Procurar'}
                  </button>
                </div>
                {searchError ? (
                  <p className={modalStyles.searchError} role="alert">
                    {searchError}
                  </p>
                ) : null}
                {selectedUser ? (
                  <div className={modalStyles.selectedDonor}>
                    <span>
                      {selectedUser.name}
                      {selectedUser.email ? ` — ${selectedUser.email}` : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={modalStyles.field}>
                <label className={modalStyles.label}>Afiliado</label>
                <div className={modalStyles.readonlyField}>{registration.affiliate_name}</div>
              </div>
            )}

            {!isEdit ? (
              <div className={modalStyles.field}>
                <span className={modalStyles.label} id="distributor-payout-label">
                  Pagamento da comissão *
                </span>
                <div
                  className={modalStyles.payoutGrid}
                  role="radiogroup"
                  aria-labelledby="distributor-payout-label"
                >
                  <label
                    className={`${modalStyles.payoutCard} ${payoutMethod === 'doey' ? modalStyles.payoutCardActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="payout"
                      className={modalStyles.payoutInputHidden}
                      checked={payoutMethod === 'doey'}
                      onChange={() => setPayoutMethod('doey')}
                    />
                    <span className={modalStyles.payoutCardInner}>
                      <span className={`${modalStyles.payoutIconWrap} ${modalStyles.payoutIconDoey}`}>
                        <Icon icon="mdi:flash" aria-hidden />
                      </span>
                      <span className={modalStyles.payoutTexts}>
                        <span className={modalStyles.payoutTitle}>Doey automático</span>
                      </span>
                    </span>
                  </label>
                  <label
                    className={`${modalStyles.payoutCard} ${payoutMethod === 'manual' ? modalStyles.payoutCardActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="payout"
                      className={modalStyles.payoutInputHidden}
                      checked={payoutMethod === 'manual'}
                      onChange={() => setPayoutMethod('manual')}
                    />
                    <span className={modalStyles.payoutCardInner}>
                      <span
                        className={`${modalStyles.payoutIconWrap} ${modalStyles.payoutIconManual}`}
                      >
                        <Icon icon="mdi:wallet-outline" aria-hidden />
                      </span>
                      <span className={modalStyles.payoutTexts}>
                        <span className={modalStyles.payoutTitle}>Manual</span>
                      </span>
                    </span>
                  </label>
                </div>
                {payoutMethod === 'doey' ? (
                  <div className={modalStyles.field}>
                    <label className={modalStyles.label} htmlFor="distributor-doey-user-id">
                      ID Doey *
                    </label>
                    <input
                      id="distributor-doey-user-id"
                      className={modalStyles.input}
                      placeholder="Preenchido pelo Gateway de Pagamento"
                      maxLength={36}
                      value={doeyUserId}
                      readOnly
                      disabled
                      aria-readonly="true"
                    />
                    <p className={modalStyles.fieldHint}>
                      Preenchido automaticamente pelo Gateway de Pagamento do usuário pesquisado.
                    </p>
                    {gatewayAlert ? (
                      <div className={modalStyles.formError} role="alert">
                        <Icon icon="mdi:alert-circle-outline" aria-hidden />
                        {gatewayAlert}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className={modalStyles.field}>
              <span className={modalStyles.label} id="distributor-campaigns-label">
                Campanhas autorizadas *
              </span>
              <div
                className={modalStyles.payoutGrid}
                role="radiogroup"
                aria-labelledby="distributor-campaigns-label"
              >
                <label
                  className={`${modalStyles.payoutCard} ${allCampaigns ? modalStyles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={modalStyles.payoutInputHidden}
                    checked={allCampaigns}
                    onChange={() => {
                      setAllCampaigns(true)
                      setSelectedCampaignIds([])
                    }}
                  />
                  <span className={modalStyles.payoutCardInner}>
                    <span className={`${modalStyles.payoutIconWrap} ${modalStyles.campaignIconAll}`}>
                      <Icon icon="mdi:bullhorn-outline" aria-hidden />
                    </span>
                    <span className={modalStyles.payoutTexts}>
                      <span className={modalStyles.payoutTitle}>Todas as campanhas</span>
                    </span>
                  </span>
                </label>
                <label
                  className={`${modalStyles.payoutCard} ${!allCampaigns ? modalStyles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={modalStyles.payoutInputHidden}
                    checked={!allCampaigns}
                    onChange={() => setAllCampaigns(false)}
                  />
                  <span className={modalStyles.payoutCardInner}>
                    <span
                      className={`${modalStyles.payoutIconWrap} ${modalStyles.campaignIconSpecific}`}
                    >
                      <Icon icon="mdi:format-list-checks" aria-hidden />
                    </span>
                    <span className={modalStyles.payoutTexts}>
                      <span className={modalStyles.payoutTitle}>Campanhas específicas</span>
                    </span>
                  </span>
                </label>
              </div>
              {!allCampaigns ? (
                <div className={modalStyles.campaignPickPanel}>
                  {linkCampaigns.length === 0 ? (
                    <p className={modalStyles.campaignPickEmpty}>
                      Nenhuma campanha disponível no seu escopo de distribuidor.
                    </p>
                  ) : (
                    <div className={modalStyles.campaignPickGrid}>
                      {linkCampaigns.map(renderCampaignCard)}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {allCampaigns ? (
              <div className={modalStyles.field}>
                <label className={modalStyles.label} htmlFor="distributor-commission">
                  Comissão (%) *
                </label>
                <input
                  id="distributor-commission"
                  className={modalStyles.input}
                  type="number"
                  step="0.1"
                  min="0.1"
                  max={globalCap}
                  placeholder="10"
                  value={commissionPercent}
                  onChange={(event) => setCommissionPercent(event.target.value)}
                />
                <span className={modalStyles.fieldHint}>
                  0,1% a {globalCap}% — teto do distribuidor nesta instituição
                </span>
              </div>
            ) : null}

            {isEdit ? (
              <div className={modalStyles.field}>
                <label className={modalStyles.toggleLabel}>
                  <span>Afiliado ativo</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    className={`${modalStyles.toggle} ${isActive ? modalStyles.toggleOn : ''}`}
                    onClick={() => setIsActive((value) => !value)}
                  >
                    <span className={modalStyles.toggleThumb} />
                  </button>
                </label>
              </div>
            ) : null}

            {formError ? (
              <div className={modalStyles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {formError}
              </div>
            ) : null}
          </div>

          <div className={modalStyles.modalFooter}>
            <button type="button" className={modalStyles.btnCancel} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={modalStyles.btnSubmit}
              disabled={
                isSubmitting ||
                (!isEdit && !canSubmitWithDoeyPayout(payoutMethod, selectedUser?.doey_user_id))
              }
            >
              {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar' : 'Credenciar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
